const trapFocusHandlers = {};

/**
 * @class A11y
 * @classdesc Functions that we use to make the accessibility experience better across our themes.
 */
class A11y {
  /**
   * Finds all of the focuable elements within the container.
   *
   * @memberof A11y.prototype
   * @param {Element} container - Container DOM element to find all focusable elements within
   *
   */
  getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"),
    );
  }

  /**
   * Removes the trapped focus from the page.
   *
   * @memberof A11y.prototype
   * @param {Element} elementToFocus - Element to be focused on after trapped focus is removed
   *
   */
  removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  }

  /**
   * Traps the focus in a particular container
   *
   * @memberof A11y.prototype
   * @param {Element} container - Container DOM element to trap focus inside of
   * @param {Element} elementToFocus - Element to be focused on first
   *
   */
  trapFocus(container, elementToFocus = container) {
    const elements = this.getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    this.removeTrapFocus();

    trapFocusHandlers.focusin = event => {
      if (event.target !== container && event.target !== last && event.target !== first) {
        return;
      }

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = event => {
      if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    elementToFocus.focus();
  }
}

export default new A11y();
